import { useCallback, useRef, useState } from "react";
import { useHarness } from "@/harness/HarnessContext";
import { LessonAction } from "@/harness/harness.types";
import { AiEngine } from "@/engines/ai/AiEngine";
import { VoiceEngine } from "@/engines/voice/VoiceEngine";
import { MemoryEngine } from "@/engines/memory/MemoryEngine";
import { HomeworkEngine } from "@/engines/homework/HomeworkEngine";
import { useUiStore } from "@/stores/uiStore";
import { useWhiteboardStore } from "@/stores/whiteboardStore";
import { useLessonStore } from "@/stores/lessonStore";
import { useSessionStore } from "@/stores/sessionStore";

// Overlap constant from the realism spec (master plan §7): writing starts
// 200-400ms after speech STARTS, not after it ends.
const OVERLAP_MS = 300;
const STUDENT_ID = "local-student"; // Phase 1: single-profile; real auth is a later phase

/**
 * Runs one full lesson end to end: AI Engine -> Harness -> Voice/Whiteboard
 * side effects -> student answers -> evaluation -> homework -> memory.
 * This is the piece that makes the Classroom page "real" rather than a
 * static preview — everything it touches (engines, stores, Harness) was
 * built in earlier passes; this hook is what actually drives them together.
 */
export function useLessonRunner() {
  const harness = useHarness();
  const setPresence = useUiStore((s) => s.setTeacherPresence);
  const setBoardContent = useWhiteboardStore((s) => s.setContent);
  const addMistake = useLessonStore((s) => s.addMistake);
  const sessionStart = useSessionStore((s) => s.start);

  const [status, setStatus] = useState<"idle" | "loading" | "running" | "awaiting-answer" | "complete" | "error">("idle");
  const [pendingQuestion, setPendingQuestion] = useState<string | null>(null);
  const [summary, setSummary] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const ai = useRef(new AiEngine()).current;
  const voice = useRef(new VoiceEngine()).current;
  const memory = useRef(new MemoryEngine()).current;
  const homework = useRef(new HomeworkEngine()).current;

  const answerResolver = useRef<((answer: string) => void) | null>(null);
  const mistakesRef = useRef<string[]>([]);
  const currentTopicRef = useRef<string>("");
  const lastQuestionRef = useRef<string>("");

  const submitAnswer = useCallback((answer: string) => {
    if (!answerResolver.current) return;
    harness.submitAnswer(answer);
    answerResolver.current(answer);
    answerResolver.current = null;
    setPendingQuestion(null);
  }, [harness]);

  const runAction = useCallback(async (action: LessonAction, nextAction?: LessonAction) => {
    switch (action.type) {
      case "speak": {
        setPresence("speaking");
        const overlapsWithNextWrite = nextAction && (nextAction.type === "write" || nextAction.type === "draw" || nextAction.type === "highlight");
        const speechDone = new Promise<void>((resolve) => {
          voice.speak(action.text, { onEnded: resolve }).catch((e) => {
            console.error("Voice synthesis failed, continuing on timer fallback:", e);
            // Even if Voxtral is unreachable (e.g. no API key configured yet in
            // this environment), the lesson keeps moving on a text-timed fallback
            // rather than hanging forever.
            setTimeout(resolve, Math.min(4000, action.text.length * 60));
          });
        });

        if (overlapsWithNextWrite) {
          // Don't block the queue on the full speech — the write step
          // that follows is allowed to start while this is still playing.
          await new Promise((r) => setTimeout(r, OVERLAP_MS));
        } else {
          await speechDone;
        }
        return;
      }

      case "write":
        setPresence("writing");
        // Route by target: a "title" write replaces the heading, anything
        // else (body/explanation/etc) appends as board body text instead
        // of clobbering the title the AI just wrote moments earlier.
        if (action.target === "title") {
          setBoardContent({ title: action.content, highlightedWord: null });
        } else {
          setBoardContent({ body: action.content, highlightedWord: null });
        }
        await new Promise((r) => setTimeout(r, 250)); // let the stroke-reveal animation play out
        return;

      case "draw":
        setPresence("writing");
        setBoardContent({ body: `[${action.shape}] ${action.refs?.join(", ") ?? ""}` });
        await new Promise((r) => setTimeout(r, 300));
        return;

      case "highlight":
        setBoardContent({ highlightedWord: action.target });
        await new Promise((r) => setTimeout(r, 200));
        return;

      case "pause": {
        const jitter = Math.random() * 200 - 100; // avoid identical pause every time, per §7
        await new Promise((r) => setTimeout(r, Math.max(0, action.ms + jitter)));
        return;
      }

      case "question":
        setPresence("listening");
        setPendingQuestion(action.prompt);
        lastQuestionRef.current = action.prompt;
        setStatus("awaiting-answer");
        return;

      case "wait_for_answer": {
        const answer = await new Promise<string>((resolve) => {
          answerResolver.current = resolve;
        });
        setStatus("running");

        try {
          const evaluation = await ai.evaluateAnswer(lastQuestionRef.current, answer);
          setPresence(evaluation.correct ? "celebrating" : "correcting");
          if (!evaluation.correct) {
            const concept = evaluation.weakConcept ?? currentTopicRef.current;
            addMistake(concept);
            mistakesRef.current.push(concept);
          }
          await new Promise((r) => setTimeout(r, 700)); // let the reaction beat land before moving on
        } catch (e) {
          console.error("Evaluation failed:", e);
        }
        return;
      }

      case "summary":
        setSummary(action.text);
        setPresence("celebrating");
        return;

      case "homework":
        // AI-declared homework items are supplementary; the real targeted
        // set comes from HomeworkEngine.generateFromMistakes below.
        return;

      default:
        return;
    }
  }, [ai, voice, addMistake, setBoardContent, setPresence]);

  const start = useCallback(async (topic: string) => {
    setStatus("loading");
    setError(null);
    setSummary(null);
    currentTopicRef.current = topic;
    mistakesRef.current = [];
    sessionStart("general", topic);
    setBoardContent({ title: null, body: null, highlightedWord: null });

    try {
      setPresence("thinking");
      const priorMistakes = await memory.getPriorMistakes(STUDENT_ID, topic);
      const actions = await ai.requestLessonActions(topic, priorMistakes);

      harness.loadActions(actions);
      setStatus("running");

      for (let i = 0; i < actions.length; i++) {
        harness.advance();
        await runAction(actions[i], actions[i + 1]);
      }

      harness.advance(); // drains queue -> Harness state becomes "complete"
      setPresence("idle");
      setStatus("complete");

      // Read fresh from the store (not a hook value) — sessionStart() above
      // set this synchronously, but a hook-subscribed value in this closure
      // would still be the PREVIOUS session's id until the next re-render.
      const sid = useSessionStore.getState().sessionId ?? crypto.randomUUID();
      await memory.recordCompletion(STUDENT_ID, sid, topic, mistakesRef.current);
      await homework.generateFromMistakes(STUDENT_ID, sid, mistakesRef.current);
    } catch (e) {
      console.error("Lesson run failed:", e);
      setError((e as Error).message);
      setStatus("error");
      setPresence("idle");
    }
  }, [ai, memory, homework, harness, runAction, sessionStart, setBoardContent, setPresence]);

  return { start, submitAnswer, status, pendingQuestion, summary, error };
}
